import { defineStore } from 'pinia'
import { OrdersListItem, OrdersPerIssuerItem, OrdersPerIssuer } from '../../typings'
import {
    GetOrdersPerIssuerProps,
    GerOrdersProps,
    getOrdersPerIssuer as getOrdersPerIssuerApi,
    getOrders as getOrdersApi,
} from '../api/portfolio'
import { OrderStatus } from '../../typings/enums'
import { ref } from 'vue'

const ORDERS_PER_ISSUER_ACCEPTED_LIMIT = 7

export interface PortfolioState {
    ordersPerIssuerAccepted: OrdersPerIssuer | null
    ordersPerIssuerAcceptedLoading: boolean
    ordersPerIssuerActiveItems: OrdersPerIssuerItem[] | null
    ordersPerIssuerActiveItemsLoading: boolean
    ordersPerIssuerCompletedItems: OrdersPerIssuerItem[] | null
    ordersPerIssuerCompletedItemsLoading: boolean
    openInvestments: OrdersListItem[] | null
    openInvestmentsLoading: boolean
}

export const usePortfolio = defineStore('portfolio', () => {
    const state = ref<PortfolioState>({
        ordersPerIssuerAccepted: null,
        ordersPerIssuerAcceptedLoading: false,
        ordersPerIssuerActiveItems: null,
        ordersPerIssuerActiveItemsLoading: false,
        ordersPerIssuerCompletedItems: null,
        ordersPerIssuerCompletedItemsLoading: false,
        openInvestments: null,
        openInvestmentsLoading: false,
    })

    async function getOrdersPerIssuer(props: GetOrdersPerIssuerProps): Promise<OrdersPerIssuer> {
        const request = await getOrdersPerIssuerApi(props)
        let issuers = request.data.issuers

        issuers = issuers.map((issuer: OrdersPerIssuerItem) => {
            return {
                name: issuer.name,
                total_amount: issuer.total_amount,
                currency: 'EUR',
                orders: issuer.orders ? issuer.orders : [],
            }
        })

        const totalAmount = issuers.reduce(
            (accumulator: number, currentValue: OrdersPerIssuerItem) => accumulator + currentValue.total_amount,
            0
        )

        const currency = Array.from(
            new Set(
                issuers.map((issuer: OrdersPerIssuerItem) => {
                    return issuer.currency
                })
            )
        )[0] as string

        return {
            total_amount: totalAmount,
            issuers: issuers,
            currency: currency,
        }
    }

    async function getOrders(props: GerOrdersProps): Promise<OrdersListItem[]> {
        const request = await getOrdersApi(props)
        const orders = request.data.orders
        return orders
    }

    async function getOrdersPerIssuerAccepted(accountId: string) {
        state.value.ordersPerIssuerAcceptedLoading = true
        state.value.ordersPerIssuerAccepted = await getOrdersPerIssuer({
            accountId: accountId,
            order: false,
            filters: {
                'filter[status]': OrderStatus.ACCEPTED.toString(),
            },
            limit: ORDERS_PER_ISSUER_ACCEPTED_LIMIT,
        })
        state.value.ordersPerIssuerAcceptedLoading = false
    }

    async function getOpenInvestments(accountId: string) {
        state.value.openInvestmentsLoading = true
        state.value.openInvestments = await getOrders({
            accountId: accountId,
            filters: {
                'filter[status]': [OrderStatus.PAID.toString(), OrderStatus.PENDING.toString()],
            },
        })
        state.value.openInvestmentsLoading = false
    }

    async function getOrdersPerIssuerActiveItems(accountId: string) {
        state.value.ordersPerIssuerActiveItemsLoading = true
        const ordersPerIssuerActiveRequest = await getOrdersPerIssuer({
            accountId: accountId,
            order: true,
            filters: {
                'filter[status]': OrderStatus.ACCEPTED.toString(),
            },
        })
        state.value.ordersPerIssuerActiveItems = ordersPerIssuerActiveRequest.issuers
        state.value.ordersPerIssuerActiveItemsLoading = false
    }

    async function getOrdersPerIssuerCompletedItems(accountId: string) {
        state.value.ordersPerIssuerCompletedItemsLoading = true

        // The COMPLETE status is not implemented yet.

        // const ordersPerIssuerCompletedRequest = await getOrdersPerIssuer({
        //     accountId: accountId,
        //     order: true,
        //     filters: {
        //         'filter[status]': OrderStatus.COMPLETED.toString(),
        //     },
        // })
        // state.value.ordersPerIssuerCompletedItems = ordersPerIssuerCompletedRequest.issuers
        state.value.ordersPerIssuerCompletedItems = []
        state.value.ordersPerIssuerCompletedItemsLoading = false
    }

    return {
        getOrdersPerIssuerAccepted,
        getOpenInvestments,
        getOrdersPerIssuerActiveItems,
        getOrdersPerIssuerCompletedItems,
        state,
    }
})
